<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="mobile-registration-form__basic-info-container">
    <div v-if="isShowEmailDisclaimer">
      <UtdNotification id="mobileEmailDisclaimer"
                       type="info"
                       notification-mode="info"
                       :has-close-notification="true"
                       @closed="isShowEmailDisclaimer = false">
        <template #title>
          {{ $t(C_ACCOUNT.FORM_MESSAGING.EMAIL_NOTIFICATION_TITLE_REDESIGN) }}
        </template>
        <template #listContent>
          <ul class="mobile-registration-form__email-notification-list">
            <li v-for="(message, index) in formattedEmailNotification"
                :key="index"
                class="mobile-registration-form__email-notification-list-item">
              <span v-html="message" />
            </li>
          </ul>
        </template>
      </UtdNotification>
    </div>
    <fieldset v-show="formFields.salutation && !formFields.salutation.isHidden"
              id="mobileSalutationField"
              class="wk-js2 mobile-registration-form-field">
      <UtdSelect id="mobileSalutationSelect"
                 :selected="form.salutation"
                 :options="SALUTATION_OPTIONS"
                 label="Title"
                 :required="formFields.salutation?.required"
                 placeholder="Select a title"
                 @input="emit('selectChange', { value: $event, name: REG_FIELD.SALUTATION })"
      />
    </fieldset>
    <div class="mobile-registration-form__attach-heading">
      <h2 class="mobile-registration-form__attach-title ds1-utd-h2">Create an account to get started</h2>
      <p class="mobile-registration-form__attach-required">All fields are required</p>
    </div>
    <fieldset v-show="formFields.firstName && !formFields.firstName.isHidden"
              id="mobileFirstNameField"
              class="wk-js2 mobile-registration-form-field">
      <UtdTextInput label="First Name"
                    :is-error="checkFieldInvalid(REG_FIELD.FIRST_NAME) && isDirty.firstName"
                    :is-disabled="!isEditableName(formFields.firstName)"
                    :error-message="$t(getFieldErrorMessage(REG_FIELD.FIRST_NAME))"
                    :required="formFields.firstName?.required">
        <input id="mobileRegistrationFirstName"
               v-model="form.firstName"
               aria-label="First Name"
               type="text"
               placeholder="Enter a first name"
               :disabled="!isEditableName(formFields.firstName)"
               :maxlength="formFields.firstName?.maxLength"
               class="wk-field-input form-field jumpstart-input"
               autocomplete="given-name"
               @input="checkResetServerError(REG_FIELD.FIRST_NAME)"
               @blur="() => isDirty.firstName = true">
      </UtdTextInput>
    </fieldset>
    <fieldset v-show="formFields.lastName && !formFields.lastName.isHidden"
              id="mobileLastNameField"
              class="wk-js2 mobile-registration-form-field">
      <UtdTextInput label="Last Name"
                    :is-error="checkFieldInvalid(REG_FIELD.LAST_NAME) && isDirty.lastName"
                    :is-disabled="!isEditableName(formFields.lastName)"
                    :error-message="$t(getFieldErrorMessage(REG_FIELD.LAST_NAME))"
                    :required="formFields.lastName?.required">
        <input id="mobileLastName"
               v-model="form.lastName"
               type="text"
               placeholder="Enter a last name"
               :disabled="!isEditableName(formFields.lastName)"
               aria-label="Last Name"
               :maxlength="formFields.lastName?.maxLength"
               class="wk-field-input form-field jumpstart-input"
               autocomplete="family-name"
               @input="checkResetServerError(REG_FIELD.LAST_NAME)"
               @blur="() => isDirty.lastName = true">
      </UtdTextInput>
    </fieldset>
    <fieldset v-show="formFields.suffix && !formFields.suffix.isHidden"
              id="mobileSuffixField"
              class="wk-js2 mobile-registration-form-field">
      <UtdTextInput label="Suffix"
                    :optional="true"
                    :is-error="false"
                    :is-disabled="!formFields.suffix?.isEditable"
                    :required="formFields.suffix?.required">
        <input id="mobileSuffix"
               v-model="form.suffix"
               type="text"
               placeholder="Enter a suffix (optional)"
               :disabled="!formFields.suffix?.isEditable"
               aria-label="Suffix"
               :maxlength="formFields.suffix?.maxLength"
               class="wk-field-input form-field jumpstart-input"
               autocomplete="honorific-suffix"
               @input="checkResetServerError('suffix')"
               @blur="() => isDirty.suffix = true">
      </UtdTextInput>
    </fieldset>
    <fieldset v-show="formFields.email && !formFields.email.isHidden"
              id="mobileEmailField"
              class="wk-js2 mobile-registration-form-field">
      <UtdTextInput label="Contact Email"
                    :is-error="shouldShowEmailError"
                    :is-disabled="!formFields.email?.isEditable"
                    :error-message="$t(getFieldErrorMessage(REG_FIELD.EMAIL))"
                    :required="formFields.email?.required">
        <input id="mobileEmail"
               v-model="form.email"
               type="email"
               placeholder="Enter a contact email"
               :disabled="!formFields.email?.isEditable"
               aria-label="Contact email"
               :maxlength="formFields.email?.maxLength"
               class="wk-field-input form-field jumpstart-input"
               autocomplete="email"
               @input="handleEmailInput"
               @blur="handleEmailBlur">
      </UtdTextInput>
    </fieldset>
    <IapEmailInUseNotification v-if="isEmailInUse"
                               @closed="isEmailInUse = false"
                               @use-different-email="handleUseDifferentEmail" />
    <MergeSuggestionNotification v-if="showMergeSuggestion"
                                 :is-ehr-user="isEhrUser"
                                 @closed="handleCloseMergeSuggestion"
                                 @merge-clicked="handleMergeClicked" />
    <fieldset v-show="formFields.phone1 && !formFields.phone1.isHidden"
              id="mobilePhone1Field"
              class="wk-js2 mobile-registration-form-field">
      <UtdPhoneInput ref="phoneInputRef"
                     label="Phone Number"
                     placeholder="Enter a phone number"
                     :options="phoneOptions"
                     :required="formFields.phone1?.required"
                     :input="phoneInput"
                     :is-dirty-override="isDirty.phone1"
                     @on-phone-ext-input="handlePhoneExtInput"
                     @on-phone-type-change="handlePhoneType"
                     @on-phone-input-blur="handlePhoneBlur"
                     @on-phone-input="handlePhoneInput" />
    </fieldset>
  </div>
</template>

<script setup>
import UtdTextInput from '_acaSrc/components/shared/input/UtdTextInput.vue';
import UtdPhoneInput from '_acaSrc/components/shared/input/UtdPhoneInput.vue';
import UtdSelect from '_acaSrc/components/shared/input/UtdSelect.vue';
import {
    C_ACCOUNT,
    UTD_WAYF_EMAIL_CHANGED_NOTIFICATION,
    isOidcRegex,
    SALUTATION_OPTIONS,
    REG_FIELD
} from '_acaSrc/utility/constants';
import UtdNotification from '_acaSrc/components/shared/utd/UtdNotification.vue';
import MergeSuggestionNotification from
    '_acaSrc/components/register/MergeSuggestionNotification.vue';
import IapEmailInUseNotification from
    '_acaSrc/components/register/inAppPurchaseRegister/IapEmailInUseNotification.vue';
import useRegistrationForm from '_acaSrc/composables/registrationForm.composable';
import useEmailCheck from '_acaSrc/composables/useEmailCheck.composable';
import { SET_SHOULD_SUGGEST_MERGE } from '_acaSrc/store/login.store.js';
import { useStore } from 'vuex';
import { ref, computed } from 'vue';

const store = useStore();

const registrationForm = computed(() => store.getters['login/registrationForm']);
const formFields = computed(() => registrationForm.value.formFields);
const shouldSuggestMerge = computed(() => registrationForm.value.shouldSuggestMerge);

const supportTag = computed(() => store.getters['footer/supportTag']);
const isSso = computed(() => isOidcRegex.test(supportTag.value.serverTag));

const emit = defineEmits([
    'selectChange',
    'close',
    'merge-clicked'
]);

const shouldShowEmailError = computed(() =>
    (checkFieldInvalid(REG_FIELD.EMAIL) && isDirty.email) || needsNewCode.value);

const phoneInput = computed(() => ({
    inputSize: 'small',
    phoneValue: form.phone1,
    phoneType: form.phone1Type
}));

const phoneOptions = computed(() => ({
    showError: true,
    showSuccess: false,
    isPhoneRequired: formFields.value.phone1?.required,
    showPhoneExt: true,
    serverError: serverError.phone1,
    isDisabled: !formFields.value.phone1?.isEditable
}));

const isShowEmailDisclaimer = ref(false);

const formattedEmailNotification = computed(() =>
    UTD_WAYF_EMAIL_CHANGED_NOTIFICATION.map(msg =>
        msg.replace('{supportTag}', supportTag.value.supportTagDisplay || '')));

const showMergeSuggestion = computed(() =>
    shouldSuggestMerge.value && !isSso.value);

const isEhrUser = computed(() => store.getters['app/isEhrUser'] ?? false);

const handleCloseMergeSuggestion = () => {
    store.commit(`login/${SET_SHOULD_SUGGEST_MERGE}`, false);
};

const handleMergeClicked = () => {
    emit('merge-clicked');
};

const isEditableName = (field) => field?.isEditable !== false;

const isEmailInUse = ref(false);

const handleEmailInput = () => {
    isEmailInUse.value = false;
    checkResetServerError(REG_FIELD.EMAIL);
};

const { checkEmailAvailability } = useEmailCheck();

const handleEmailBlur = async() => {
    isDirty.email = true;
    if (!form.email || checkFieldInvalid(REG_FIELD.EMAIL)) {
        return;
    }
    try {
        const isAvailable = await checkEmailAvailability(form.email);
        if (isAvailable === false) {
            isEmailInUse.value = true;
        }
        else {
            isEmailInUse.value = false;
            store.commit(`login/${SET_SHOULD_SUGGEST_MERGE}`, false);
        }
    }
    catch {
        isEmailInUse.value = false;
    }
};

const handleUseDifferentEmail = () => {
    form.email = '';
    isEmailInUse.value = false;
    isDirty.email = false;
};

const phoneInputRef = ref(null);

const handlePhoneInput = (value) => {
    form.phone1 = value;
    checkResetServerError(REG_FIELD.PHONE1);
};

const handlePhoneExtInput = (value) => {
    form.phone1Ext = value;
};

const handlePhoneType = (value) => {
    form.phone1Type = value;
};

const handlePhoneBlur = () => {
    isDirty.phone1 = true;
};

const {
    form,
    isDirty,
    serverError,
    needsNewCode,
    checkResetServerError,
    checkFieldInvalid,
    getFieldErrorMessage
} = useRegistrationForm();
</script>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

.mobile-registration-form__basic-info-container {
  :deep(.wk-field-input) {
    background-color: @DS1-UTD-PRIMARY-BG-COLOR;
  }
}

.xAIMobile {
  .mobile-registration-form__basic-info-container {
    :deep(.wk-field-input) {
      background-color: @WKCE-WHITE;
      border-radius: 50px;
      border: 1px solid @WKCE-CREAM;
    }

    :deep(.wk-select__input) {
      background-color: @WKCE-WHITE;
      border-radius: 50px;
      border: 1px solid @WKCE-CREAM;
    }

    :deep(.wk-field-label) {
      color: @WKCE-CREAM-SHADE2;
      font-size: 14px;
      font-weight: 600;
    }
  }
}
</style>

